import type {
  TargetingContextualSegmentCreateParams,
  TargetingContextualSegmentItemCreateParams,
} from '@openx/types/gqlTypes';
import { type ContextualSegmentsDataPayload, contextualSegmentPrefix } from '@openx/types/targeting/contextualSegments';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import { groupCustomFormatByProvider } from './utils/enrichment';

export const removeNotAllowedSymbols = (text: string): string => {
  const notAllowedInputSymbols = /[^a-zA-Z0-9\s-_]/g;

  return text.trim().replace(notAllowedInputSymbols, '');
};

/** Groups the V1 contextual segments list per provider (a provider without segment ids yet is kept). */
export const prepareContextualSegments = (
  contextualSegments: CustomVariablesDataPayload[]
): ContextualSegmentsDataPayload[] => {
  const groupedData = contextualSegments.reduce<Record<string, ContextualSegmentsDataPayload>>(
    (acc, { attr, op, val }) => {
      acc[attr] ??= { attr, excludes: '', includes: '' };

      if (op === ComparisonType.INTERSECTS) {
        acc[attr].includes = val;
      } else if (op === ComparisonType.NOT_INTERSECTS) {
        acc[attr].excludes = val;
      }

      return acc;
    },
    {}
  );

  return Object.values(groupedData);
};

/** Reverse of `prepareContextualSegments`. */
export const convertContextualToCustom = (
  contextualSegments: ContextualSegmentsDataPayload[]
): CustomVariablesDataPayload[] =>
  contextualSegments.flatMap(({ attr, includes, excludes }) => {
    if (!includes.length && !excludes.length) {
      return [{ attr, op: ComparisonType.INTERSECTS, val: '' }];
    }

    return [
      ...(includes.length ? [{ attr, op: ComparisonType.INTERSECTS, val: includes }] : []),
      ...(excludes.length ? [{ attr, op: ComparisonType.NOT_INTERSECTS, val: excludes }] : []),
    ];
  });

type ContextualSegmentApiItem = TargetingContextualSegmentItemCreateParams;

/** Maps form contextual segments to the `contextual_segment` API shape, grouped per provider. */
export const mapContextualSegmentsToApi = (
  contextualSegments?: CustomVariablesDataPayload[] | null
): TargetingContextualSegmentCreateParams | undefined => {
  const selections = groupCustomFormatByProvider(contextualSegments ?? []);
  const toApiItems = (inclusionType: 'includes' | 'excludes'): ContextualSegmentApiItem[] =>
    selections
      .filter(selection => selection[inclusionType].length)
      .map(selection => ({ provider: selection.providerId, val: selection[inclusionType] }));

  const includes = toApiItems('includes');
  const excludes = toApiItems('excludes');

  if (!includes.length && !excludes.length) return undefined;

  return {
    ...(includes.length && { includes }),
    ...(excludes.length && { excludes }),
  };
};

export const mapContextualSegmentsToFormState = (
  contextualSegment?: TargetingContextualSegmentCreateParams | null
): CustomVariablesDataPayload[] => {
  const toFormState = (items: ContextualSegmentApiItem[] | null | undefined, op: ComparisonType) =>
    (items ?? [])
      .filter(({ val }) => val?.length)
      .map(({ provider, val }) => ({ attr: provider, op, val: val.join(',') }));

  return [
    ...toFormState(contextualSegment?.includes, ComparisonType.INTERSECTS),
    ...toFormState(contextualSegment?.excludes, ComparisonType.NOT_INTERSECTS),
  ];
};

const legacyContextualOps: string[] = [ComparisonType.INTERSECTS, ComparisonType.NOT_INTERSECTS];

/**
 * Splits API custom variables into real custom variables and legacy contextual segments
 * (`ctx_<provider>` attributes, returned with the prefix removed).
 * `ctx_` entries with other ops cannot be represented as contextual segments, so they stay in `custom`; callers
 * loading a package drop them (`packageToFormState`), since MGMT drops every `ctx_` entry on a `new` write.
 */
export const splitLegacyContextualSegments = (
  customVariables?: CustomVariablesDataPayload[] | null
): { contextual: CustomVariablesDataPayload[]; custom: CustomVariablesDataPayload[] } =>
  (Array.isArray(customVariables) ? customVariables : []).reduce(
    (result, customVariable) => {
      if (customVariable.attr.startsWith(contextualSegmentPrefix) && legacyContextualOps.includes(customVariable.op)) {
        result.contextual.push({ ...customVariable, attr: customVariable.attr.slice(contextualSegmentPrefix.length) });
      } else {
        result.custom.push(customVariable);
      }

      return result;
    },
    { contextual: [] as CustomVariablesDataPayload[], custom: [] as CustomVariablesDataPayload[] }
  );
